import React, { useCallback, useEffect, useState } from 'react';
// --- Types ---
interface Product {
  title: string;
  editionLabel?: string;
  priceText: string;
  thumbnailUrl: string;
}
interface ToastData {
  name: string;
  city: string;
  product: Product;
  timeAgo: string;
}
// --- Data ---
const NAMES = ['María', 'Laura', 'Sofía', 'Camila', 'Valentina', 'Martina', 'Lucía', 'Paula', 'Florencia', 'Agustina', 'Micaela', 'Julieta', 'Rocío', 'Carolina', 'Daniela', 'Ana', 'Milagros', 'Victoria', 'Sol', 'Bárbara', 'Macarena', 'Guadalupe'];
const CITIES = ['CABA', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Mar del Plata', 'Salta', 'Santa Fe', 'San Juan', 'Neuquén', 'Tucumán', 'Bahía Blanca', 'Resistencia', 'Posadas', 'Jujuy', 'Bariloche', 'Comodoro Rivadavia'];
const TIMES = ['hace unos segundos', 'hace 1 minuto', 'hace 2 minutos', 'hace 5 minutos'];
const FALLBACK_PRODUCTS: {
  product: Product;
  weight: number;
}[] = [{
  product: {
    title: 'Kit Digital 33 Rituales',
    priceText: '$16.990 ARS',
    thumbnailUrl: 'https://cdn.shopify.com/s/files/1/0752/3211/6894/files/BUNDLE.png?v=1772835860'
  },
  weight: 70
}, {
  product: {
    title: 'Kit Digital + El Altar Digital',
    priceText: '$25.980 ARS',
    thumbnailUrl: 'https://cdn.shopify.com/s/files/1/0752/3211/6894/files/BUNDLE.png?v=1772835860'
  },
  weight: 30
}];
// --- Helper Functions ---
const getRandomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const getWeightedRandomProduct = (products: {
  product: Product;
  weight: number;
}[]): Product => {
  const totalWeight = products.reduce((sum, item) => sum + item.weight, 0);
  let random = Math.random() * totalWeight;
  for (const item of products) {
    if (random < item.weight) return item.product;
    random -= item.weight;
  }
  return products[0].product;
};
export function RecentPurchaseToast() {
  const [isVisible, setIsVisible] = useState(false);
  const [toastData, setToastData] = useState<ToastData | null>(null);
  const [scannedProducts, setScannedProducts] = useState<Product[]>([]);
  const [usedNames, setUsedNames] = useState<string[]>([]);
  // 1. Scan DOM for products on mount
  useEffect(() => {
    // A simple scanner that looks for the main product images and prices
    // In a real generic scenario this would be more complex, but for this funnel
    // we know the exact structure, so we'll mostly rely on the accurate fallbacks.
    const products: Product[] = [];
    // Try to find the bundle image
    const bundleImg = document.querySelector('img[src*="BUNDLE.png"]') as HTMLImageElement;
    if (bundleImg) {
      products.push({
        title: 'Kit Digital 33 Rituales',
        priceText: '$16.990 ARS',
        thumbnailUrl: bundleImg.src
      });
    }
    setScannedProducts(products);
  }, []);
  // 2. Generate random toast data
  const generateToast = useCallback(() => {
    // Select name avoiding last 10
    let availableNames = NAMES.filter(n => !usedNames.includes(n));
    if (availableNames.length === 0) {
      availableNames = NAMES; // Reset if we ran out
      setUsedNames([]);
    }
    const name = getRandomItem(availableNames);
    // Update used names history (keep last 10)
    setUsedNames(prev => {
      const next = [...prev, name];
      if (next.length > 10) next.shift();
      return next;
    });
    const city = getRandomItem(CITIES);
    const timeAgo = getRandomItem(TIMES);
    // Select product (use scanned if available, else fallback)
    let product: Product;
    if (scannedProducts.length > 0 && Math.random() > 0.5) {
      product = getRandomItem(scannedProducts);
    } else {
      product = getWeightedRandomProduct(FALLBACK_PRODUCTS);
    }
    setToastData({
      name,
      city,
      product,
      timeAgo
    });
    setIsVisible(true);
    // Hide after 4 seconds
    setTimeout(() => {
      setIsVisible(false);
    }, 4000);
  }, [scannedProducts, usedNames]);
  // 3. Manage timing loop
  useEffect(() => {
    // First toast after 3 seconds
    const initialTimer = setTimeout(() => {
      generateToast();
    }, 3000);
    return () => clearTimeout(initialTimer);
  }, []); // Run once on mount
  useEffect(() => {
    // If it just hid, schedule the next one
    if (!isVisible && toastData !== null) {
      // Random interval between 8 and 15 seconds
      const nextInterval = Math.floor(Math.random() * (15000 - 8000 + 1)) + 8000;
      const timer = setTimeout(() => {
        generateToast();
      }, nextInterval);
      return () => clearTimeout(timer);
    }
  }, [isVisible, generateToast, toastData]);
  if (!toastData) return null;
  return <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-[360px] transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 pointer-events-none'} motion-reduce:transition-opacity motion-reduce:duration-300 motion-reduce:translate-y-0`} aria-live="polite" data-id="element-496">
      <div className="bg-[#1a0a2e]/95 backdrop-blur-md border border-[rgba(196,160,232,0.2)] shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_16px_rgba(123,79,166,0.2)] rounded-xl p-3 flex items-center gap-3.5" data-id="element-497">
        {/* Product Image */}
        <div className="w-[52px] h-[52px] flex-shrink-0 rounded-lg overflow-hidden bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] flex items-center justify-center" data-id="element-498">
          <img src={toastData.product.thumbnailUrl} alt={toastData.product.title} className="w-full h-full object-cover" data-id="element-499" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-500">
          <div className="flex items-center justify-between gap-2 mb-0.5" data-id="element-501">
            <div className="text-[13px] text-white font-medium truncate" data-id="element-502">
              {toastData.name} de {toastData.city}
            </div>
            <div className="text-[10px] text-[var(--muted)] whitespace-nowrap" data-id="element-503">
              {toastData.timeAgo}
            </div>
          </div>

          <div className="text-[12px] text-[var(--text)] leading-tight line-clamp-2 mb-1" data-id="element-504">
            Compró{' '}
            <strong className="text-white font-medium" data-id="element-505">
              {toastData.product.title}
            </strong>
          </div>

          <div className="flex items-center gap-1.5" data-id="element-506">
            <div className="flex items-center gap-0.5 text-[var(--gold)]" data-id="element-507">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" data-id="element-508">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-509" />
              </svg>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" data-id="element-510">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-511" />
              </svg>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" data-id="element-512">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-513" />
              </svg>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" data-id="element-514">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-515" />
              </svg>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" data-id="element-516">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-517" />
              </svg>
            </div>
            <span className="text-[11px] font-bold text-[var(--gold-l)]" data-id="element-518">
              {toastData.product.priceText}
            </span>
          </div>
        </div>
      </div>
    </div>;
}